import { CSSProperties } from 'react';

interface HiddenUsernameFieldProps {
  email?: string | null;
}

// Clipped rather than `display: none` — hidden inputs are the weakest signal to
// autofill heuristics, and a clipped field still occupies the layout tree.
// Inlined so the component works the same on Bootstrap and Tailwind pages
// without depending on which stylesheet the host page loaded.
const clippedStyle: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * An off-screen `autocomplete="username"` input carrying the account's email.
 *
 * Password managers save a password against a username. On forms where the
 * email is fixed — shown as plain text, or in a `disabled` input, which autofill
 * skips — there is no field to pair the password with, so the credential gets
 * saved without an identity or not offered at all. This supplies one without
 * putting another editable box in front of the user.
 *
 * Kept out of the tab order and hidden from screen readers: the email is
 * already presented visibly on these pages, so this is purely a machine-facing
 * hint. `readOnly` (not `disabled`) because autofill ignores disabled inputs.
 */
export const HiddenUsernameField = ({ email }: HiddenUsernameFieldProps) => {
  if (!email) {
    return null;
  }

  return (
    <input
      type="text"
      name="username"
      autoComplete="username"
      value={email}
      readOnly
      tabIndex={-1}
      aria-hidden="true"
      style={clippedStyle}
    />
  );
};
